import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { Panel } from 'react-bootstrap'
import styled from 'styled-components'
import FAQHeading from './FAQHeading'
import FAQSection from './FAQSection'

const PrimaryStyledPanel = styled(Panel)`
  border: 0;
  box-shadow: none;

  .faq-category {
    border: 0;
    border-top: 1px solid var(--clr-grey-6-to-text);
  }

  .toggle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    text-decoration: none;
    color: var(--clr-grey-4-to-white);
    font-size: var(--h5);
    position: relative;

    .arrow {
      transform: rotateZ(180deg);
      transition: transform 300ms;
      fill: var(--clr-accent);
    }
  }
  .toggle.collapsed {
    color: var(--clr-grey-3-to-text);
    &:hover {
      color: var(--clr-accent-to-white);
    }
    .arrow {
      fill: var(--clr-grey-3-to-accent);
      transform: rotateZ(0deg);
      transition: transform 300ms;
    }
  }
`

const PrimaryWrapper = styled.div`
  border: 1px solid var(--clr-grey-6-to-text);

  & + & {
    border-top: 0;
  }

  .panel-title {
    outline: none;
  }
`

const TertiaryWrapper = styled.div`
  .faq-text {
    margin: 15px;
  }
  .faq-text a {
    color: var(--grey-3);
    text-decoration: underline;
  }

  .panel-default {
    border: none;
    margin: 0;
    box-shadow: none;
  }

  .panel-heading {
    background: var(--clr-white-to-grey-1);
    padding: 0;
    cursor: pointer;
    color: var(--clr-accent-to-black);
    border: none;
    border-top: 1px solid var(--clr-accent-to-white);
    border-radius: 0;
  }

  .faq-primary {
    border-right: 1px solid var(--clr-grey-6-to-text);
    border-left: 1px solid var(--clr-grey-6-to-text);
    border-radius: 0;
  }

  .faq-primary:last-of-type {
    border-bottom: 1px solid var(--clr-grey-6-to-text);
    border-radius: 0;
  }

  .panel-heading a {
    display: inline-block;
    width: 100%;
    padding: 10px 20px;
    text-decoration: none;
    position: relative;
    font-size: var(--h5);
    font-weight: 700;
  }

  .faq-panel > .panel-heading > .panel-title > a {
    padding-left: 35px;
  }

  .faq-panel > .panel-heading > .panel-title > a:before {
    font-family: 'FontAwesome';
    position: absolute;
    content: '\\f0D7';
    left: 15px;
    top: 10px;
    display: block;
    transition: transform 300ms;
    color: var(--clr-accent);
    font-weight: 400;
  }

  .faq-panel > .panel-heading > .panel-title > a.collapsed:before {
    transform: rotateZ(-90deg);
  }

  .panel-heading .collapsed {
    color: var(--clr-grey-3-to-text);
    font-weight: 400;
  }

  .panel-heading .collapsed:hover {
    color: var(--clr-accent);
  }
  
  .panel-body {
    padding: 0;
  }

  .faq-panel .panel-body {
    padding: 5px 20px;
    background-color: var(--clr-grey-7-to-black);
  }

  @media (max-width: 992px) {
    .faq-panel .panel-body {
      padding: 5px;
    }
    .faq-primary {
      border-right: none;
    }
  }
  
  @media (max-width: 575px) {
    #quick-feedback .body {
      float: none;
    }
  }
`


const FAQPanel = ({
  panelClassName, // "faq-panel"
  toggleFaq,
  faqId,
  open,
  headingStyle = {},
  toggleClassName,
  tabIndex,
  name,
  iconSrc,
  createUserEvent,
  category, // 'faqs'
  action, // 'TogglePrimaryCategory' | 'ToggleSecondaryCategory'
  target, // category name
  bodyStyle = {},
  children,
  hideCategories
}) => {
  const primary = action === 'TogglePrimaryCategory'
  const secondary = action === 'ToggleSecondaryCategory'
  
  return primary ? (
    <PrimaryWrapper>
      <PrimaryStyledPanel
        className={`${panelClassName || ''}`}
        defaultExpanded={open}
      >
      <FAQSection 
        tabIndex={tabIndex}
        createUserEvent={createUserEvent}
        category={category}
        action={action}
        target={target}
        bodyStyle={bodyStyle}
        headingStyle={headingStyle}
        toggleClassName={toggleClassName}
        name={name}
        iconSrc={iconSrc}
        children={children}
      />
      </PrimaryStyledPanel>
    </PrimaryWrapper>
  ) : secondary ? (
    <Panel className={`${panelClassName || ''}`} defaultExpanded={open}>
      <FAQSection 
        tabIndex={tabIndex}
        createUserEvent={createUserEvent}
        category={category}
        action={action}
        target={target}
        bodyStyle={bodyStyle}
        headingStyle={headingStyle}
        toggleClassName={toggleClassName}
        name={name}
        iconSrc={iconSrc}
        children={children}
      />
    </Panel>
  ) : (
    <TertiaryWrapper>
      <div className={`${hideCategories ? 'faq-primary' : ''}`}>
        <div id="content" className={open ? 'active' : ''}>
          <Panel
            className={`${panelClassName || ''}`}
            onToggle={toggleFaq(faqId)}
            expanded={open}
          >
            <Fragment>
              <FAQHeading
                tabIndex={tabIndex}
                headingStyle={headingStyle}
                toggleClassName={toggleClassName}
                name={name}
                iconSrc={iconSrc}
              />
              <Panel.Collapse>
                <Panel.Body style={bodyStyle}>{children}</Panel.Body>
              </Panel.Collapse>
            </Fragment>
          </Panel>
        </div>
      </div>
    </TertiaryWrapper>
  )
}

FAQPanel.propTypes = {
  tabIndex: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  headingStyle: PropTypes.object,
  iconSrc: PropTypes.string,
  toggleClassName: PropTypes.string,
  createUserEvent: PropTypes.func.isRequired,
  category: PropTypes.string,
  action: PropTypes.string,
  target: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  bodyStyle: PropTypes.object,
  children: PropTypes.node,
  toggleFaq: PropTypes.func,
  faqId: PropTypes.number,
  hideCategories: PropTypes.bool
}

export default FAQPanel



// WEBPACK FOOTER //
// ./src/components/FAQ/FAQPanel.js